import clsx from "clsx";
import { get } from "lodash";
import * as yup from "yup";
import React, { FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useLocation } from "react-router-dom";

import { getFormatDate } from "~/libs/getFormatDate";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  LangType,
  ReportType,
  useAddNotificationMutation,
  useGetDealersCitiesQuery,
  useGetDealersFederalAreaQuery,
  useGetGroupsToolsQuery,
  useGetReportPersonalLazyQuery,
  useGetReportPlaceByLocationLazyQuery,
  useGetReportPlaceLazyQuery,
  useGetReportPostByPostLazyQuery,
  useGetReportPostsByPlaceLazyQuery,
  useGetReportRequiredsToolsLazyQuery,
  useGetReportRequiredToolsLazyQuery,
  useGetReportServiceLazyQuery,
  useGetReportToolsInsuranceByIdLazyQuery,
  useGetReportToolsInsuranceLazyQuery,
  useGetReportToolsLazyQuery,
  useGetReportToolsMetrologicalLazyQuery,
  useGetReportTradePremisesByIdLazyQuery,
  useGetReportWarehouseLazyQuery,
  useSetChangeStatusZdGlobalForDealerMutation,
  useSetDuplicatePersonalMutation,
  useSetDuplicateRequiredToolsMutation,
  useSetDuplicateToolsMutation,
  useSetMakeVisibleMutation,
  ZdGlobalInfoForDealerStatus,
  ZdGroupType,
} from "~/schemas/generated/graphql";

import Table from "~/components/Table";
import Modal from "~/shared/ui/Fields/Modal";
import ReportZDForm from "~/components/Forms/ReportZDForm";
import Button from "~/shared/ui/Fields/Button";
import Preloader from "~/components/Preloader";
import TextArea from "~/shared/ui/Fields/TextArea";

import Krest from "~/assets/img/svg/krest.png";
import Galka from "~/assets/img/svg/galka.png";
import Krug from "~/assets/img/svg/krug.png";

import { STATUS_TYPES } from "~/constans/statuses";
import { NOTIFICATIONS } from "~/constans/notifications";
import { NUMBER_OPTIONS } from "~/pages/ZD/TableReducerZD/options";

import s from "./style.less";
import Pagination from "~/components/Pagination";
import { onScrollBottomDealersFO } from "~/hooks/selectDealerValueFederalArea";
import PlanReportFilesForm from "~/components/Forms/PlanReportFilesForm";
import { usePermissions } from "~/shared/context/PermissionContext";
import { getZDUserAccess } from "~/pages/Editor/EditorServiceAuthorization/options";
import { useZD } from "~/entity/ZD/hooks/useZD";
import useRequestHandler from "~/shared/hooks/useRequestHandler";
import { useZdRequests } from "~/shared/hooks/useZDRequests/useZDRequests";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";

interface ITableFactory {
  selectedService;
  options;
  // report;
  items;
  loading;
  total;
  placeLoading;
  change;
  refetch;
  deleteMut;
  // reportSelect;
  hashReport;
  commentsPlanReport;
  selectPlaces;
  statusDealerOne;
  statusDealerOnePremises;
  statusQuery;
  placeReport;
  reportPlace;
}

const TableReducerZD: FC<ITableFactory> = ({
  selectedService,
  options,
  // report,
  items,
  statusDealerOne,
  statusDealerOnePremises,
  change,
  total = 0,
  loading,
  placeLoading,
  refetch,
  deleteMut,
  // reportSelect,
  hashReport,
  statusQuery,
  commentsPlanReport,
  selectPlaces,
  reportPlace = () => [undefined, undefined],
  placeReport,
}) => {
  const { t } = useTranslation();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const { hasEdit, hasAnyCheck } = getZDUserAccess(
    hasPermission,
    selectedService
  );
  // Режим «только просмотр»: есть права на просмотр (zd.common.get /
  // zd.planReports.get), но нет прав на редактирование/проверку текущего пункта ЗД
  const isOnlyView = !hasEdit && !hasAnyCheck;
  const [descriptionError, setDescriptionError] = useState("");
  const { PlanReportId } = useURLParamsGetter<{ PlanReportId: string }>();
  const location = useLocation();
  const isPlanReportPage = location.pathname.includes("/zd/plan_report");
  const {
    fullname,
    openModal,
    currentId,
    event,
    sortField,
    sortSubField,
    count,
    dateBegin,
  } = useGetLocationData();

  const canGetReports = hasPermission("zd.reports.get");
  const currentDealerId = event || sessionStorage.getItem("currentDealerId");
  let language = localStorage.getItem("language");
  const isEn = language === "en";
  const noCaching = sessionStorage.getItem("noCaching");

  const toolsGroup = useGetGroupsToolsQuery({
    variables: {
      groupType: ZdGroupType.GroupOne,
    },
    fetchPolicy: "cache-and-network",
  });

  const arrToolsGroup = get(toolsGroup, "data.ToolsGetter.getGroups", []).map(
    (el) => {
      return {
        value: el,
        label: el,
      };
    }
  );

  const defaultStateVal = {
    loading: false,
    items: [],
    total: 0,
    refetch: () => {},
  };
  const [actualData, setActualData] = useState<any>(defaultStateVal);
  const requests = useZdRequests({
    currentDealerId,
    page: 0,
    count,
    dateBegin,
    selectedService,
  });

  useEffect(() => {
    if (requests) {
      setActualData((p) => ({ ...p, ...requests }));
    }
  }, [requests]);

  useEffect(() => {
    setActualData(defaultStateVal);
  }, [selectedService]);

  const { reportSelect, loadingReportSelect } = getItemsReport(selectedService);

  function getItemsReport(selectedService) {
    return actualData.items.length
      ? {
          reportSelect: get(actualData, `items`, []).map((data) => {
            return {
              value: data.UUID,
              label:
                selectedService === "toolsInsurance"
                  ? `ЛИ:${data.NumberList}/ ПР:${data.NumberDetail}/ ${
                      isEn ? data.NameEn : data.Name
                    }`
                  : isEn
                  ? data.NameEn
                  : data.Name,
            };
          }),
          loadingReportSelect: actualData.loading,
        }
      : {
          reportSelect: [],
          loadingReportSelect: true,
        };
  }

  const shcemaForm = yup.object().shape({
    CertificationDate: yup.string().required(t("validation.required")),
    FactContract: yup.object().shape({
      label: yup
        .string()
        .min(1, t("validation.more250"))
        .required(t("validation.required")),
      value: yup.string().nullable().required(t("validation.required")),
    }),
  });
  const shcemaFormReport = yup.object().shape(
    (() => {
      const obj = {};

      if (openModal !== "") {
        obj["cities"] = yup.object().shape({
          label: yup.string().min(1, t("validation.more250")),
          value: yup.string().nullable(),
        });

        if (!!sortSubField) {
          obj["FO"] = yup.object().shape({
            label: yup
              .string()
              .min(1, t("validation.more250"))
              .required(t("validation.required")),
            value: yup.string().nullable().required(t("validation.required")),
          });
        } else {
          obj["FO"] = yup.object().shape({
            label: yup.string().min(1, t("validation.more250")),
            value: yup.string().nullable(),
          });
        }
      }

      if (
        (openModal === "Excel" &&
          selectedService !== "placePosts" &&
          selectedService !== "warehouses" &&
          selectedService !== "services" &&
          selectedService !== "places" &&
          selectedService !== "toolsInsurance" &&
          selectedService !== "requiredTools" &&
          selectedService !== "personal" &&
          selectedService !== "tools" &&
          selectedService !== "tradePremises" &&
          selectedService !== "toolsMetrological") ||
        (selectedService === "placePosts" && openModal === "ExcelPlace") ||
        (selectedService === "tools" && openModal === "ExcelPlace")
      ) {
        obj["services"] = yup.object().shape({
          label: yup
            .string()
            .min(1, t("validation.more250"))
            .required(t("validation.required")),
          value: yup.string().nullable().required(t("validation.required")),
        });
      } else {
        obj["services"] = yup.object().shape({
          label: yup.string().min(1, t("validation.more250")),
          value: yup.string().nullable(),
        });
      }
      return obj;
    })()
  );

  const shemaFormRequared = yup.object().shape({
    services: yup.array().nullable(),
    // .min(1, t("validation.required"))
    // .required(t("validation.required")),
  });

  const [addNotificationMutation] = useAddNotificationMutation({});
  const { control, handleSubmit, setValue, errors } = useForm({
    resolver: openModal === "CertificationDate" && yupResolver(shcemaForm),
  });

  const serviceSelectForm = useForm({
    resolver: yupResolver(
      selectedService === "requiredTools" && openModal === "ExcelPlace"
        ? shemaFormRequared
        : shcemaFormReport
      // shcemaFormReport
    ),
  });

  const isMultiWatch =
    (selectedService === "requiredTools" &&
      openModal === "ExcelPlace" &&
      serviceSelectForm.watch("services")) ||
    [];

  const schema = yup.object().shape({
    description: yup.string().required(t("validation.required")),
  });
  const commentForm = useForm({
    resolver: yupResolver(schema),
  });
  const projectForm = useForm({});
  const addStatusForm = useForm({});
  const setData = useSetLocationData();

  useEffect(() => {
    if (commentsPlanReport) {
      commentForm.setValue("description", commentsPlanReport?.reason || "");
    }
  }, [commentsPlanReport]);

  let serviceActiveValue = serviceSelectForm.watch("services");
  let FOActiveValue = serviceSelectForm.watch("FO");
  let countryActiveValue = serviceSelectForm.watch("country");
  let citiesActiveValue = serviceSelectForm.watch("cities");

  useEffect(() => {
    if (openModal !== "") {
      const foEng = (val) =>
        val ? (val === "Ничего не выбрано" ? "" : val) : "";

      setData(
        "sortField",
        foEng(FOActiveValue && FOActiveValue.value),
        "sortSubField",
        foEng(citiesActiveValue && citiesActiveValue.value)
      );
    } else {
      setData("sortField", "", "sortSubField", "");
    }
  }, [FOActiveValue, citiesActiveValue]);

  const federalArea = useGetDealersFederalAreaQuery({
    variables: {
      cityEn:
        selectedService !== "personal"
          ? isEn && sortSubField
            ? sortSubField
            : undefined
          : undefined,
      city:
        selectedService !== "personal"
          ? !isEn && sortSubField
            ? sortSubField
            : undefined
          : undefined,
    },
  });

  const citiesData = useGetDealersCitiesQuery({
    variables: {
      FOEn: isEn && sortField ? sortField : undefined,
      FO: !isEn && sortField ? sortField : undefined,
    },
  });

  const dealerFederalArea = get(
    federalArea,
    `data.DealerGetter.getDealersFederalArea.items${isEn ? "En" : ""}`,
    []
  ).map((item) => ({
    label: item,
    value: item,
  }));

  const dealerCities = get(
    citiesData,
    `data.DealerGetter.getDealersCities.items${isEn ? "En" : ""}`,
    []
  ).map((item) => ({
    label: item,
    value: item,
  }));

  const handleCloseModal = () => {
    setDescriptionError("");
    setData(
      "openModal",
      "",
      "currentId",
      "",
      "sortField",
      "",
      "sortSubField",
      ""
    );
  };

  const dealersForSelect = onScrollBottomDealersFO();

  const [addValues] = change({
    onCompleted() {
      setData(
        "openModal",
        "",
        "currentId",
        "",
        "sortField",
        "",
        "sortSubField",
        ""
      );
      refetch();
    },
    onError({ graphQLErrors }) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: graphQLErrors[0].message,
        },
      });
    },
  });

  const [deleteMutation] = deleteMut({
    variables: { id: String(currentId) },
    onCompleted() {
      refetch();
    },
  });

  const [makeVisible] = useSetMakeVisibleMutation({
    onCompleted() {
      setDescriptionError("");
      setData("openModal", "", "currentId", "");
      refetch();
      handleCloseModal();
    },
    onError: (error) => {
      const errorMessage = error.message.split(":")[1].trim();
      const normalize = `${errorMessage[0].toUpperCase()}${errorMessage.substr(
        1
      )}`;
      setDescriptionError(normalize);
    },
  });

  const [getReportServiceByIdLazyQuery, ReportServiceByIdLazyQuery] =
    useGetReportServiceLazyQuery({
      onCompleted(data) {
        const hash =
          hashReport && get(data, `ZdReportsGetter.ServiceById.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportPersonalLazyQuery, ReportPersonalLazyQuery] =
    useGetReportPersonalLazyQuery({
      onCompleted(data) {
        const hash =
          hashReport && get(data, `ZdReportsGetter.Personal.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [
    getReportRequiredToolsByIdLazyQuery,
    ReportRequiredToolsByIdLazyQuery,
  ] = useGetReportRequiredToolsLazyQuery({
    onCompleted(data) {
      const hash =
        hashReport && get(data, `ZdReportsGetter.RequiredToolsById.fileHash`);
      hash &&
        addNotificationMutation({
          variables: {
            status: STATUS_TYPES.FORMATION,
            type: NOTIFICATIONS.report,
            id: hash,
          },
        });
    },
    fetchPolicy: "no-cache",
  });

  const [
    getReportToolsInsuranceByIdLazyQuery,
    ReportToolsInsuranceByIdLazyQuery,
  ] = useGetReportToolsInsuranceByIdLazyQuery({
    onCompleted(data) {
      const hash =
        hashReport && get(data, `ZdReportsGetter.ToolsInsuranceById.fileHash`);
      hash &&
        addNotificationMutation({
          variables: {
            status: STATUS_TYPES.FORMATION,
            type: NOTIFICATIONS.report,
            id: hash,
          },
        });
    },
    fetchPolicy: "no-cache",
  });

  const [
    getReportToolsMetrologicalByIdLazyQuery,
    ReportToolsMetrologicalByIdLazyQuery,
  ] = useGetReportToolsMetrologicalLazyQuery({
    onCompleted(data) {
      const hash =
        hashReport &&
        get(data, `ZdReportsGetter.ToolsMetrologicalById.fileHash`);
      hash &&
        addNotificationMutation({
          variables: {
            status: STATUS_TYPES.FORMATION,
            type: NOTIFICATIONS.report,
            id: hash,
          },
        });
    },
    fetchPolicy: "no-cache",
  });

  const [getReportToolsByIdLazyQuery, ReportToolsByIdLazyQuery] =
    useGetReportToolsLazyQuery({
      onCompleted(data) {
        const hash =
          hashReport && get(data, `ZdReportsGetter.ToolsById.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportWarehouseByIdLazyQuery, ReportWarehouseByIdLazyQuery] =
    useGetReportWarehouseLazyQuery({
      onCompleted(data) {
        const hash =
          hashReport && get(data, `ZdReportsGetter.WarehouseById.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportPlaceByIdLazyQuery, ReportPlaceByIdLazyQuery] =
    useGetReportPlaceLazyQuery({
      onCompleted(data) {
        const hash =
          hashReport && get(data, `ZdReportsGetter.PlaceById.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportPostsByPlaceLazyQuery, ReportPostsByPlaceLazyQuery] =
    useGetReportPostsByPlaceLazyQuery({
      onCompleted(data) {
        const hash =
          hashReport && get(data, `ZdReportsGetter.PostByPlaceIds.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportPostLazyQuery, ReportPostLazyQuery] =
    useGetReportPostByPostLazyQuery({
      onCompleted(data) {
        const hash = placeReport && get(data, `ZdReportsGetter.Posts.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportPlaceByLocationLazyQuery, ReportPlaceByLocationLazyQuery] =
    useGetReportPlaceByLocationLazyQuery({
      onCompleted(data) {
        const hash = get(data, `ZdReportsGetter.PlaceByLocation.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportToolsInsuranceLazyQuery, ReportToolsInsuranceLazyQuery] =
    useGetReportToolsInsuranceLazyQuery({
      onCompleted(data) {
        const hash = get(
          data,
          `ZdReportsGetter.ToolsInsuranceByPlace.fileHash`
        );
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportRequiredsToolsLazyQuery, ReportRequiredsToolsLazyQuery] =
    useGetReportRequiredsToolsLazyQuery({
      onCompleted(data) {
        const hash = get(data, `ZdReportsGetter.RequiredToolsByIds.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const [getReportServiceLazyQuery, ReportServiceLazyQuery] =
    useGetReportTradePremisesByIdLazyQuery({
      onCompleted(data) {
        const hash = get(data, `ZdReportsGetter.TradePremisesById.fileHash`);
        hash &&
          addNotificationMutation({
            variables: {
              status: STATUS_TYPES.FORMATION,
              type: NOTIFICATIONS.report,
              id: hash,
            },
          });
      },
      fetchPolicy: "no-cache",
    });

  const reconstructData = (items ?? []).reduce((accFirst, el) => {
    accFirst = accFirst || [];

    accFirst = [
      ...accFirst,
      Object.keys(el).reduce((acc, key) => {
        if (NUMBER_OPTIONS.includes(key)) {
          acc[key] =
            typeof el[key] === "number"
              ? el[key] === 0
                ? {
                    value: 0,
                    label: "not_provided",
                    icon: Krest,
                  }
                : {
                    value: 1,
                    label: "provided",
                    icon: Galka,
                  }
              : {
                  value: "",
                  label: "нету",
                  icon: Krug,
                };
          return acc;
        }

        // for date view
        if (key === "ActualFrom") {
          acc[key] = getFormatDate(new Date(el[key]), true);
          return acc;
        }
        // for Name En
        if (key === "Name") {
          acc[key] = isEn ? el["NameEn"] : el[key];
          return acc;
        }
        if (key === "groupArea") {
          acc[key] = `zd.${el[key]}`;
          return acc;
        }
        acc[key] = el[key];
        return acc;
      }, {}),
    ];

    return accFirst;
  }, []);

  const arrGroup = [];

  const isArrData =
    (selectedService === "tools" ||
      selectedService === "requiredTools" ||
      selectedService === "toolsInsurance" ||
      selectedService === "tradePremises" ||
      selectedService === "places" ||
      selectedService === "personal") &&
    reconstructData &&
    reconstructData.map((el) => {
      if (selectedService === "requiredTools")
        return { ...el, isOneParrent: false };
      if (arrGroup.includes(el.GroupOne)) {
        return {
          ...el,
          isOneParrent: false,
        };
      } else {
        arrGroup.push(el.GroupOne);
        return {
          ...el,
          isOneParrent: true,
        };
      }
    });

  const arrSrotTollsIn =
    selectedService === "tools" ||
    selectedService === "requiredTools" ||
    selectedService === "toolsInsurance" ||
    selectedService === "places" ||
    selectedService === "tradePremises" ||
    selectedService === "personal"
      ? isArrData.map((el) => {
          return {
            ...el,
            list: el.list,
            emptyList: el.emptyList,
            // parrent: isParrent(el),
            // parentNumber: parrentNumber(el),
          };
        })
      : [];

  const [updatedReconstructData, setUpdatedReconstructData] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);

  useEffect(() => {
    function emptyListRow(elem) {
      const { list, emptyList } = elem;
      if (emptyList.length !== 0) {
        list.push(emptyList.shift());
        return emptyListRow(elem);
      }

      setCurrentRow(null);
      return elem;
    }

    const x =
      JSON.stringify(updatedReconstructData) !== JSON.stringify(arrSrotTollsIn);
    if (["tools", "requiredTools", "personal"].includes(selectedService) && x) {
      if (currentRow) {
        setUpdatedReconstructData(
          arrSrotTollsIn.map((el) => {
            if (el.UUID === currentRow) {
              emptyListRow(el);
            }
            return el;
          })
        );
      } else {
        setUpdatedReconstructData(arrSrotTollsIn);
      }
    }
  }, [reconstructData, currentRow, loading, refetch]);

  const [dublicate] = useSetDuplicateToolsMutation({
    onCompleted() {
      refetch();
    },
  });

  const [dublicatePersonal] = useSetDuplicatePersonalMutation({
    onCompleted() {
      refetch();
    },
  });

  const [duplicateRequiredTools] = useSetDuplicateRequiredToolsMutation({
    onCompleted() {
      refetch();
    },
  });

  const arrInclude = ["Total", "Area", "Year"];
  const arrIncludeCombine = ["PlacesCombine", "Combine"];
  const arrDateTime = ["PositionStart", "StudyCertificateDate"];
  const arrStatus = [
    { label: t("zd.draft"), value: "draft" },
    { label: t("zd.processing"), value: "processing" },
    { label: t("zd.rejected"), value: "rejected" },
    { label: t("zd.confirmed"), value: "confirmed" },
  ];

  const serviceReports = () => {
    setData("openModal", "Excel");
  };

  const serviceReportsPlace = () => {
    setData("openModal", "ExcelPlace");
  };

  const { setCompliance, updateComment } = useZD();

  const onSubmitRest =
    ({ zdPersonalId, instanceId }) =>
    (data) => {
      setCompliance({
        zdPersonalId: zdPersonalId as string,
        instanceId: instanceId as string,
        compliance: !!data.value as boolean,
        dealerId: currentDealerId as string,
      }).then(() => {
        setData("openModal", "");
        refetch();
      });
    };

  const onSubmit = (data) => {
    const onAll = {
      DealerId: currentDealerId,
      UUID: currentId,
    };

    const day = openModal === "CheckedDate" && data[openModal].getDate();
    const month = openModal === "CheckedDate" && data[openModal].getMonth() + 1;
    const year = openModal === "CheckedDate" && data[openModal].getFullYear();

    const date = openModal === "CheckedDate" && `${day}.${month}.${year}`;
    const objectVar = {};

    objectVar[openModal] =
      typeof data.value === "number"
        ? data.value
        : arrInclude.includes(openModal)
        ? Number(data[openModal])
        : arrDateTime.includes(openModal)
        ? new Date(data[openModal])
        : arrIncludeCombine.includes(openModal)
        ? data.value
        : openModal === "CheckedDate"
        ? date
        : data[openModal];

    if (openModal !== undefined && openModal === "CertificationDate") {
      addValues({
        variables: {
          ...onAll,
          ...objectVar,
          FactContract: Number(data.FactContract.value),
        },
      });
    } else {
      addValues({
        variables: {
          ...onAll,
          ...objectVar,
        },
      });
    }
  };

  const selectIsParentTools = updatedReconstructData
    ? updatedReconstructData
        .filter((el) => el.parrent)
        .map((data) => {
          return {
            value: data.UUID,
            label: isEn ? data.NameEn : data.Name,
          };
        })
    : [];
  const selectIsChildrenTools = updatedReconstructData
    ? updatedReconstructData
        .filter((el) => !el.parrent && !el.forUUID)
        .map((data) => {
          return {
            value: data.UUID,
            label: isEn ? data.NameEn : data.Name,
          };
        })
    : [];

  const [reDuplicate, setReDuplicate] = useState("");
  const onSumbitDublicate = (data) => {
    if (selectedService === "requiredTools") {
      const updatedList = reconstructData.reduce((acc, element) => {
        if (element.UUID === data) {
          if (element.emptyList.length > 0) {
            element.list.push(element.emptyList.shift());
            return [...acc, element];
          } else {
            duplicateRequiredTools({ variables: { uuid: data } });
            setReDuplicate(data);
          }
        }
        return [...acc, element];
      }, []);

      setUpdatedReconstructData(updatedList);
      return;
    }
    setUpdatedReconstructData(
      arrSrotTollsIn.map((el) => {
        if (el.UUID === data) {
          const { list, emptyList } = el;
          if (emptyList.length > 0) {
            list.push(emptyList.shift());
          } else {
            if (selectedService === "tools") {
              dublicate({
                variables: {
                  uuid: data,
                },
              });
            } else if (selectedService === "personal") {
              dublicatePersonal({
                variables: {
                  uuid: data,
                },
              });
            }

            setCurrentRow(el.UUID);
          }
        }
        return el;
      })
    );
  };

  useEffect(() => {
    if (items.length && reDuplicate) {
      onSumbitDublicate(reDuplicate);
      setReDuplicate("");
    }
  }, [items]);

  const arrObjects =
    (selectedService === "places" || selectedService === "warehouses") &&
    reconstructData.map(({ UUID, Name, NameEn }) => ({
      value: UUID,
      label: isEn ? NameEn : Name,
    }));

  const getServiceReports = (data) => {
    const baseParams = {
      Type: ReportType.Excel,
      lang: LangType.Ru,
      Russia: true,
    };

    if (data.FO.value) {
      baseParams["FO"] = data.FO.value;
    }
    if (selectedService === "personal") {
      if (data.dealerIdFilter.value) {
        baseParams["DealerIds"] = [data.dealerIdFilter.value];
      }
    }

    if (data.cities.value) {
      baseParams["City"] = data.cities.value;
    }

    if (selectedService !== "personal") {
      if (serviceActiveValue.value) {
        baseParams[selectedService === "tools" ? "groupOne" : "UUIDs"] =
          selectedService === "tools"
            ? serviceActiveValue.value
            : [serviceActiveValue.value];
      }
    }

    switch (selectedService) {
      case "services":
        getReportServiceByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "requiredTools":
        getReportRequiredToolsByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "personal":
        getReportPersonalLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "toolsInsurance":
        getReportToolsInsuranceByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "toolsMetrological":
        getReportToolsMetrologicalByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "tools":
        getReportToolsByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "warehouses":
        getReportWarehouseByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "places":
        getReportPlaceByIdLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "placePosts":
        getReportPostLazyQuery({
          // getReportPostsByPlaceLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
      case "tradePremises":
        getReportServiceLazyQuery({
          // getReportPostsByPlaceLazyQuery({
          variables: {
            ...baseParams,
          },
        });
        break;
    }

    handleCloseModal();
  };

  const getPlaceReports = (data) => {
    const baseParams = {};

    if (data.FO && data.FO.value) {
      baseParams["FO"] = data.FO.value;
    }

    if (data.cities && data.cities.value) {
      baseParams["City"] = data.cities.value;
    }
    if (data.services && data.services.value) {
      baseParams["UUIDs"] = [data.services.value];
    }

    switch (selectedService) {
      case "placePosts":
        getReportPostsByPlaceLazyQuery({
          // getReportPostLazyQuery({
          variables: {
            ...baseParams,
            Type: ReportType.Excel,
            lang: LangType.Ru,
            Russia: true,
          },
        });
        break;
      case "places":
        getReportPlaceByLocationLazyQuery({
          variables: {
            Russia: true,
            ...baseParams,
            Type: ReportType.Excel,
            lang: LangType.Ru,
          },
        });
        break;
      case "tools":
        getReportToolsByIdLazyQuery({
          variables: {
            ...baseParams,
            Type: ReportType.Excel,
            lang: LangType.Ru,
          },
        });
        break;
      case "toolsInsurance":
        getReportToolsInsuranceLazyQuery({
          variables: {
            Russia: true,
            ...baseParams,
            Type: ReportType.Excel,
            lang: LangType.Ru,
          },
        });
        break;
      case "tradePremises":
        getReportServiceLazyQuery({
          variables: {
            Russia: true,
            ...baseParams,
            Type: ReportType.Excel,
            lang: LangType.Ru,
          },
        });
        break;
      case "requiredTools":
        const base = {};

        let loft =
          !!serviceActiveValue &&
          serviceActiveValue.length > 0 &&
          serviceActiveValue.map(({ value }) => value);

        if (loft.filter((el) => el).length === loft.length) {
          base["UUIDs"] = loft;
        }

        getReportRequiredsToolsLazyQuery({
          variables: {
            // UUIDs: loft,
            ...base,
            Type: ReportType.Excel,
            lang: LangType.Ru,
          },
        });
        break;
    }
    handleCloseModal();
  };

  let flof = selectedService === "placePosts" &&
    !placeLoading &&
    reconstructData.length > 0 && [...reconstructData, ...selectPlaces];

  const findParent = (id) => flof && flof.find((x) => x.parrentNewId === id);

  const getLevel = (id, lvl) => {
    return id ? getLevel(findParent(id)?.ZD_PlacesId, lvl + 1) : lvl;
  };

  let newInput =
    !placeLoading &&
    flof &&
    flof.length > 0 &&
    flof.map(
      ({
        parrentNewId,
        id,
        ActualFrom,
        ActualTo,
        ZD_PlacesId,
        Compliance,
        Name,
        NameEn,
        Total,
        UUID,
        planReportId,
      }) => {
        const idObj = {};

        if (parrentNewId) {
          idObj["parrentNewId"] = parrentNewId;
        } else {
          idObj["id"] = id;
        }
        return {
          ...idObj,
          ActualFrom,
          ActualTo,
          Compliance,
          Name,
          NameEn,
          Total,
          UUID,
          planReportId,
          ZD_PlacesId: findParent(ZD_PlacesId)
            ? findParent(ZD_PlacesId).parrentNewId
            : null,
          lvl: getLevel(ZD_PlacesId, 0),
        };
      }
    );

  let arrSorts = (newInput) => {
    let arr = [];
    flof &&
      newInput.forEach((el, i) => {
        if (el.ZD_PlacesId === undefined) {
          arr.push(el);
          const childs = newInput.filter(
            (elem) => elem.ZD_PlacesId === el.parrentNewId
          );
          arr = [...arr, ...childs];
        }
      });
    return arr;
  };

  const deleteIndicator = () => {
    deleteMutation();
    handleCloseModal();
  };

  const descriptionWatch = commentForm.watch("description");

  const { requestWithNotifHandler } = useRequestHandler();

  useEffect(() => {
    if (typeof descriptionWatch === "string") {
      descriptionWatch.trim().length > 0 && setDescriptionError("");
    }
  }, [descriptionWatch]);

  const setParams = useURLParamsSetter();
  const makeVisibleButRest = ({ description }) => {
    const list = (items ?? []).map((el) => el.list).flat();

    const target = list.find((el) => `${el.zdPersonalId}` === currentId);
    requestWithNotifHandler({
      request: async () => {
        return await updateComment({
          zdPersonalId: target.zdPersonalId,
          instanceId: target.instanceId,
          comment: description,
        });
      },
      callback: async () => {
        try {
          await addNotificationMutation({
            variables: {
              status: STATUS_TYPES.CONFIRM,
              type: NOTIFICATIONS.success,
              content: "Комментарий обновлен.",
            },
          });
          setParams({
            openModal: undefined,
            currentId: undefined,
          });
        } catch (e) {
          console.log(e);
        }
      },
    });
  };

  const makeVisibleBut = ({ description }) => {
    if (description.trim().length > 0) {
      makeVisible({
        variables: {
          PlanReportId: PlanReportId,
          reason: description.trim(),
        },
      });
    } else {
      setDescriptionError(t("zd.description"));
    }
  };

  const [arrStatusMutation] = useSetChangeStatusZdGlobalForDealerMutation({
    onCompleted() {
      setData("openModal", "");
      refetch();
      statusQuery.refetch();
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("common.сhange_was_successful"),
        },
      });
    },
    onError({ graphQLErrors }) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: graphQLErrors[0].message,
        },
      });
    },
  });

  const changeStatusMutation = (status: ZdGlobalInfoForDealerStatus) => {
    arrStatusMutation({
      variables: {
        DealerId: currentDealerId,
        status: status,
        isPremises: selectedService === "tradePremises" ? true : false,
      },
    });
  };

  const isReportCountry =
    (selectedService === "places" && openModal === "ExcelPlace") ||
    selectedService === "personal" ||
    (selectedService === "toolsInsurance" && openModal === "ExcelPlace");

  const getFileLink = (targetArray: any[]) => {
    return targetArray && currentId
      ? targetArray.reduce((acc, element) => {
          if (
            element.UUID === currentId ||
            element.planReportId === currentId ||
            element.zdPersonalId === currentId ||
            element.ZD_UUID === currentId
          ) {
            return selectedService === "notes"
              ? element?.Image || []
              : element?.Files || [];
          } else {
            // Проверяем zdPersonalId и ZD_UUID в элементах list
            if (!acc.length && element.list?.length) {
              const foundInList = element.list.find((el: any) =>
                el.zdPersonalId === currentId ||
                el.UUID === currentId ||
                el.planReportId === currentId ||
                el.ZD_UUID === currentId
              );
              if (foundInList) {
                return selectedService === "notes"
                  ? foundInList?.Image || []
                  : foundInList?.Files || [];
              }
              return getFileLink(element.list);
            }
            return acc;
          }
        }, [])
      : [];
  }

  const getTarget = (targetArray: any[]): any => {
    if (!targetArray?.length || !currentId) return undefined;
    for (const element of targetArray) {
      if (
        element.UUID === currentId ||
        element.planReportId === currentId ||
        element.zdPersonalId === currentId ||
        element.ZD_UUID === currentId
      ) {
        return element;
      }
      if (element.list?.length) {
        // Проверяем zdPersonalId и ZD_UUID в элементах list
        const foundInList = element.list.find((el: any) =>
          el.zdPersonalId === currentId ||
          el.UUID === currentId ||
          el.planReportId === currentId ||
          el.ZD_UUID === currentId
        );
        if (foundInList) return foundInList;
        // Рекурсивный поиск в nested list
        const found = getTarget(element.list);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };

  const filesLinks = getFileLink(reconstructData);
  const instance = getTarget(reconstructData);

  const data = (
    flof
      ? arrSorts(newInput)
      : (["tools", "requiredTools"].includes(selectedService) &&
          updatedReconstructData) ||
        (selectedService === "toolsInsurance" && arrSrotTollsIn) ||
        (selectedService === "places" && arrSrotTollsIn) ||
        (selectedService === "tradePremises" && arrSrotTollsIn) ||
        reconstructData
  ).map((el) => ({ ...el, currentDealerId, rowType: selectedService }));

  return (
    <>
      {canGetReports && hashReport && (
        <Button
          txtBtn={t("zd.button")}
          small
          green
          customClassName={clsx(s.correct_margin)}
          onClick={serviceReports}
          loading={loadingReportSelect}
        />
      )}

      {canGetReports &&
        (placeReport ||
          selectedService === "places" ||
          selectedService === "toolsInsurance" ||
          selectedService === "tools" ||
          selectedService === "requiredTools") && (
          <Button
            txtBtn={`${t(`zd.button_${selectedService}`)}`}
            small
            green
            customClassName={clsx(s.correct_margin)}
            onClick={serviceReportsPlace}
          />
        )}

      {!statusQuery.loading && (
        <div className={s.wrapperStatusBtn}>
          <div className={s.statusTitle}>
            Статус заявки:{" "}
            {selectedService === "tradePremises"
              ? t(`zd.${statusDealerOnePremises}`)
              : selectedService !== "tradePremises"
              ? t(`zd.${statusDealerOne}`)
              : t(`zd.${statusDealerOne}`)}
          </div>

          <>
            {selectedService === "tradePremises" &&
              hasPermission("zd.submitCommercialAreas") && (
                <Button
                  txtBtn={"Отправить анкету на рассмотрение"}
                  onClick={() =>
                    changeStatusMutation(ZdGlobalInfoForDealerStatus.Processing)
                  }
                  orange
                  small
                />
              )}

            {((selectedService === "tradePremises" &&
              hasPermission("zd.rejectSaleAreas")) ||
              (selectedService !== "tradePremises" &&
                hasPermission("zd.rejectWithoutSaleAreas"))) && (
              <>
                <Button
                  txtBtn={"Принять"}
                  // onClick={addArrStatusMutationTrue}
                  onClick={() =>
                    changeStatusMutation(ZdGlobalInfoForDealerStatus.Confirmed)
                  }
                  orange
                  small
                />
                <Button
                  txtBtn={"Отклонить"}
                  // onClick={addArrStatusMutationFalse}
                  onClick={() =>
                    changeStatusMutation(ZdGlobalInfoForDealerStatus.Rejected)
                  }
                  orange
                  small
                />
              </>
            )}
          </>
        </div>
      )}

      {placeLoading && loading ? (
        <Preloader />
      ) : (
        <Pagination showRightAll total={total}>
          <Table
            setValue={setValue}
            options={options}
            selectParent={selectedService}
            items={data}
            objectArr={arrObjects}
            refetch={refetch}
            type={"zd"}
            localizationName={selectedService}
            handleSubmit={isOnlyView ? false : handleSubmit}
            loading={fullname === "PlacePosts" ? placeLoading : loading}
            sortable={false}
            control={control}
            errors={errors}
            onSubmit={
              isOnlyView
                ? false
                : selectedService === "personal"
                ? onSubmitRest
                : onSubmit
            }
            onSumbitDublicate={
              isOnlyView
                ? false
                : ["tools", "personal", "requiredTools"].includes(
                    selectedService
                  ) && onSumbitDublicate
            }
            customClassName={s.firstTable}
          />
        </Pagination>
      )}
      <Modal
        isOpen={openModal === "Excel" || openModal === "ExcelPlace"}
        title={t(
          `zd.report_${
            openModal === "ExcelPlace"
              ? `${selectedService}_excel`
              : selectedService
          }`
        )}
        w={750}
        actionClose={handleCloseModal}
      >
        <ReportZDForm
          reportSelect={
            selectedService === "tools" && openModal === "Excel"
              ? arrToolsGroup
              : reportSelect
          }
          isMultiWatchClick={isMultiWatch}
          selectPlaces={placeReport && selectPlaces}
          control={serviceSelectForm.control}
          fo={
            dealerFederalArea && [
              { label: t("zd.nothingSelected"), value: null },
              ...dealerFederalArea,
            ]
          }
          cities={
            dealerCities && [
              { label: t("zd.nothingSelected"), value: null },
              ...dealerCities,
            ]
          }
          menuCustom={true}
          handleSubmit={serviceSelectForm.handleSubmit}
          selectedService={selectedService}
          dealersForSelect={dealersForSelect}
          errors={serviceSelectForm.errors}
          isMulti={
            openModal === "ExcelPlace" && selectedService === "requiredTools"
          }
          reprotFO={isReportCountry}
          getReports={
            openModal === "Excel" ? getServiceReports : getPlaceReports
          }
          // loading={ReportPlaceLazyQuery && ReportPlaceLazyQuery.loading}
        />
      </Modal>
      <Modal
        isOpen={
          (selectedService === "personal" && openModal === "Files") ||
          (selectedService === "notes" && openModal === "Image")
        }
        title={t("planReport.planReportFiles")}
        actionClose={handleCloseModal}
      >
        <PlanReportFilesForm
          setCloseModal={handleCloseModal}
          Files={filesLinks}
          instance={instance}
          selectedService={selectedService}
          isPlanReport={isPlanReportPage}
          // isResolved={isResolovedIndex}
          refetch={refetch}
        />
      </Modal>

      <Modal
        isOpen={openModal === "Sure"}
        title={t("users.user_del")}
        actionClose={handleCloseModal}
      >
        <Button txtBtn={t("ui.cancel")} empty onClick={handleCloseModal} />
        <Button txtBtn={t("ui.delete")} onClick={deleteIndicator} />
      </Modal>
      <Modal
        isOpen={openModal === "Comment"}
        title={t("ui.aComment")}
        actionClose={handleCloseModal}
      >
        <form
          className={s.formComment}
          onSubmit={commentForm.handleSubmit(
            selectedService === "personal" ? makeVisibleButRest : makeVisibleBut
          )}
        >
          <TextArea
            name="description"
            label={t("ui.comment_title")}
            control={commentForm.control}
            error={commentForm.errors?.description?.message || descriptionError}
            vertical
          />
          <Button txtBtn={t("ui.cancel")} empty onClick={handleCloseModal} />
          <Button txtBtn={t("ui.comment")} isForm />
        </form>
      </Modal>
    </>
  );
};
export default TableReducerZD;
